/**
 * Browser errors that turn up in ordinary traffic and say nothing about our code.
 *
 * Shared by the browser reporter and the server endpoint: a tab left open across
 * a deploy keeps sending whatever its old bundle let through, so the server
 * checks again.
 *
 * Keep the patterns exact. A loose one (say "Cannot read properties of
 * undefined") silently hides real bugs.
 */
const IGNORED_MESSAGES: RegExp[] = [
  // A cross-origin script (GTM, Metrika, the Telegram widget) failed and the
  // browser withholds every detail.
  /^Script error\.?$/i,
  // Benign layout notification, not a failure.
  /ResizeObserver loop/i,
  // The visitor's connection dropped, or a navigation cancelled the request.
  /^(TypeError: )?Failed to fetch$/i,
  /^(TypeError: )?Load failed$/i,
  /^(TypeError: )?NetworkError when attempting to fetch resource\.?$/i,
  /^cancelled$/i,
  /^The (operation was|user) aborted/i,
  // A tab from before a deploy asks for a chunk that no longer exists. The
  // router reloads the page once on its own.
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  // Hydration mismatch that React recovered from by rendering on the client.
  // In the field this is nearly always an extension or in-page translator
  // rewriting the DOM, and it arrives without a usable location.
  /Minified React error #(418|419|421|422|423|425)\b/,
  /Hydration failed because/i,
  /There was an error while hydrating/i,
  // An extension injected the same classic script into the page twice. Our
  // bundle is ES modules, where a duplicate declaration fails the build, and
  // the error arrives with no stack frame to tell it apart.
  /^(SyntaxError: )?Identifier '[\w$]+' has already been declared$/,
];

const IGNORED_NAMES = ["AbortError"];

// Our code runs from script files: /assets/*.js in a build, /src/*.tsx and
// /node_modules/.vite/**/*.js under the dev server.
const SCRIPT_PATH = /\.(m?js|jsx|tsx?)$/;

function isOwnScript(url: string, origin: string): boolean {
  // The trailing slash matters: "https://lotin.uz.evil.com" starts with our origin.
  if (!url.startsWith(`${origin}/`)) return false;
  try {
    // Drop the ":line:column" a stack frame ends with.
    const { pathname } = new URL(url.replace(/(:\d+){1,2}$/, ""));
    return SCRIPT_PATH.test(pathname);
  } catch {
    return false;
  }
}

/**
 * True when no frame of the stack is in one of our scripts: a browser extension
 * or a third-party script threw inside our page.
 *
 * A frame that points at the page itself doesn't count as ours. WebKit gives
 * scripts an in-app browser injects (the Google app on iOS) the page's URL,
 * at line numbers our HTML doesn't even have.
 *
 * Neither does a Chrome frame with no URL at all ("at fn (<anonymous>:1:2768)"):
 * code injected as a string, which is how extensions that wrap built-ins like
 * Object.getOwnPropertyDescriptor run in the page.
 */
export function isThirdPartyStack(
  stack: string | undefined,
  origin: string,
): boolean {
  if (!stack) return false;
  if (/\b(chrome|moz|safari-web)-extension:\/\//.test(stack)) return true;
  const urls = stack.match(/https?:\/\/[^\s)]+/g);
  if (!urls) return /^\s+at .*<anonymous>/m.test(stack);
  return !urls.some((url) => isOwnScript(url, origin));
}

export function isIgnoredClientError(error: {
  message: string;
  name?: string;
  status?: number;
}): boolean {
  // A 4xx from the API is the normal user flow (validation, expired session).
  if (error.status !== undefined && error.status >= 400 && error.status < 500) {
    return true;
  }
  if (error.name && IGNORED_NAMES.includes(error.name)) return true;
  return IGNORED_MESSAGES.some((pattern) => pattern.test(error.message));
}
